"use client";

import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const categoryBackdrops = ["bg-accent", "bg-secondary", "bg-accent", "bg-secondary"];
function ScribbleUnderline() {
  return <svg aria-hidden="true" viewBox="0 0 220 14" preserveAspectRatio="none" className="absolute -bottom-2 left-0 h-3 w-full text-primary" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 8.5C30 3.5 60 11 90 6.5C120 2 150 10.5 180 5.5C195 3 210 7 217 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>;
}
function CategoriesGrid() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div data-index={i} key={i} className="aspect-[3/4] bg-muted animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center">
        <p className="text-muted-foreground mb-4">
          {"We couldn't load the flavors right now."}
        </p>
        <Button onClick={refetch} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
          Try again
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return <p className="text-center text-muted-foreground">
        No flavors to show just yet.
      </p>;
  }
  return <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
      {terms.map((term, i) => {
      const backdrop = categoryBackdrops[i % categoryBackdrops.length];
      const count = (term as any).count ?? 0;
      return <Link data-index={i} key={term.id} to={term.link || "#"} className="group block">
            <div className={`${backdrop} relative flex aspect-square items-center justify-center overflow-hidden p-6 transition-transform duration-[240ms] ease-out group-hover:-translate-y-1`} style={{
          boxShadow: "0 0 0 0 transparent",
          transition: "transform 240ms ease-out, box-shadow 240ms ease-out"
        }}>
              {term.image_url ? <img src={term.image_url} alt={term.name} loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-[240ms] ease-out group-hover:scale-[1.03]" /> : <div className="font-serif text-5xl font-medium text-foreground/80 transition-transform duration-[240ms] ease-out group-hover:scale-[1.03]">
                  {term.name.charAt(0)}
                </div>}
            </div>
            <div className="mt-4 px-1">
              <h3 className="font-default text-lg font-semibold tracking-tight text-foreground md:text-xl">
                {term.name}
              </h3>
              <p className="mt-1 text-sm font-medium text-muted-foreground">
                {count > 0 ? `${count} varieties` : "Explore the collection"}
              </p>
            </div>
          </Link>;
    })}
    </div>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 4,
  order: "asc",
  orderby: "name"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function Gallery() {
  return <section data-section-id="6181" id="the-flavor-gallery" className="bg-background py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-[720px] text-center">
          <h2 className="font-serif font-medium text-foreground text-[clamp(2rem,4.2vw,3.75rem)] leading-[1.04] tracking-[-0.02em]">
            Select a{" "}
            <span className="relative inline-block">
              <span className="italic font-normal">flavor</span>
              <ScribbleUnderline />
            </span>{" "}
            to begin
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
            Artisanal scoops, velvety textures, and hand-crafted inclusions. 
            Choose a category to discover our latest seasonal creations.
          </p>
        </div>

        <div className="mt-12 md:mt-16">
          <EcommerceTermsProvider className="palette-categories-wrapper" wp_term_query={wp_term_query}>
            <CategoriesGrid />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>;
}